import { useSortable } from "@dnd-kit/sortable";
import { EllipsisVertical, Pencil, Calendar } from "lucide-react";
import { CSS } from "@dnd-kit/utilities";
import { STATUS_STYLES, PERMISSION_TYPES } from "../kanban-board";

export function TaskCard({
  task,
  columnId,
  onClick,
  onEdit,
  canEdit,
  dragHandle = true,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: task._id,
      data: { columnId },
      disabled: !dragHandle,
    });

  const handleProps = dragHandle ? listeners : {};
  const attrProps = dragHandle ? attributes : {};

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attrProps}
      className={`relative flex flex-col gap-2 p-4 bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 ${
        dragHandle 
          ? "hover:border-indigo-200 cursor-grab hover:shadow-md" 
          : "cursor-pointer"
      }`}
      onClick={() => onClick && onClick(task)}
      {...handleProps}
    >
      {/* Top section with title and drag handle */}
      <div className="flex items-start justify-between">
        <h3 className="font-medium text-gray-800 line-clamp-2">{task.title}</h3>
        {dragHandle && (
          <div className="flex-shrink-0 pt-1 text-gray-400 cursor-grab">
            <EllipsisVertical size={16} />
          </div>
        )}
      </div>

      {/* Task metadata section */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="bg-green-50 text-green-600 font-medium px-2 py-0.5 rounded-full">
          {task.taskPoints} IU
        </span>
        {task.permissionType && (
          <span className="bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 font-medium rounded-full">
            {PERMISSION_TYPES[task.permissionType]?.badge || PERMISSION_TYPES[task.permissionType]?.label}
          </span>
        )}
        {task.dueDate && (
          <span className="inline-flex items-center bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
            <Calendar className="h-3 w-3 mr-1" />
            {new Date(task.dueDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        )}
      </div>

      {/* Skills tags if available */}
      {task.skills && task.skills.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {task.skills.slice(0, 2).map((skill, index) => (
            <span
              key={index}
              className="bg-blue-50 text-blue-600 text-[10px] px-1.5 py-0.5 rounded"
            >
              {skill}
            </span>
          ))}
          {task.skills.length > 2 && (
            <span className="bg-gray-50 text-gray-500 text-[10px] px-1.5 py-0.5 rounded">
              +{task.skills.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Status and edit footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <span
          className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs text-white capitalize ${
            STATUS_STYLES[task.status].bg
          }`}
        >
          <span
            className={`h-2 w-2 rounded-full ${STATUS_STYLES[task.status].dot}`}
          />
          {task.status.replace("_", " ")}
        </span>

        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(task);
            }}
            className="shrink-0 text-gray-400 hover:text-indigo-600 bg-gray-100 hover:bg-indigo-50 p-1 rounded-full transition-colors"
            title="Edit task"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
